// Notificações do navegador (Web Push / VAPID) para os avisos de integração.
//
// Tudo aqui degrada em silêncio: navegador sem suporte, permissão negada ou
// VAPID não configurado no servidor devolvem um estado, nunca uma exceção que
// derrube a tela de configurações.
import { apiFetch } from './api';

export type PushState =
  | 'unsupported' // navegador sem Service Worker / Push API (Safari antigo, etc.)
  | 'unavailable' // servidor sem chaves VAPID
  | 'denied' // usuário bloqueou nas permissões do site
  | 'off' // suportado e permitido, mas não inscrito
  | 'on'; // inscrito neste navegador

interface PushConfig {
  enabled: boolean;
  public_key: string;
}

export function pushSupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

async function config(): Promise<PushConfig | null> {
  const r = await apiFetch('GET', '/push/key');
  return r.ok ? (r.data as PushConfig) : null;
}

async function registration(): Promise<ServiceWorkerRegistration | null> {
  if (!pushSupported()) return null;
  try {
    return await navigator.serviceWorker.register('/sw.js');
  } catch {
    return null;
  }
}

/** Estado atual do push neste navegador. */
export async function pushState(): Promise<PushState> {
  if (!pushSupported()) return 'unsupported';
  const cfg = await config();
  if (!cfg?.enabled || !cfg.public_key) return 'unavailable';
  if (Notification.permission === 'denied') return 'denied';
  const reg = await registration();
  if (!reg) return 'unsupported';
  const sub = await reg.pushManager.getSubscription();
  return sub ? 'on' : 'off';
}

/** Pede permissão, inscreve o navegador e registra no servidor. */
export async function enablePush(): Promise<PushState> {
  if (!pushSupported()) return 'unsupported';
  const cfg = await config();
  if (!cfg?.enabled || !cfg.public_key) return 'unavailable';

  const perm = await Notification.requestPermission();
  if (perm !== 'granted') return perm === 'denied' ? 'denied' : 'off';

  const reg = await registration();
  if (!reg) return 'unsupported';

  // Reusa a inscrição existente: reinscrever gera outro endpoint e o servidor
  // passaria a ter duas linhas para o mesmo navegador.
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true, // exigido pelos navegadores; push silencioso é barrado
      applicationServerKey: urlBase64ToUint8Array(cfg.public_key),
    });
  }
  const r = await apiFetch('POST', '/push/subscribe', sub.toJSON());
  if (!r.ok) {
    // Servidor recusou: desfaz a inscrição local para não ficar um navegador
    // "inscrito" que nunca receberá nada.
    await sub.unsubscribe().catch(() => {});
    return 'off';
  }
  return 'on';
}

/** Cancela a inscrição deste navegador (local e no servidor). */
export async function disablePush(): Promise<PushState> {
  if (!pushSupported()) return 'unsupported';
  const reg = await registration();
  const sub = await reg?.pushManager.getSubscription();
  if (sub) {
    await apiFetch('POST', '/push/unsubscribe', { endpoint: sub.endpoint });
    await sub.unsubscribe().catch(() => {});
  }
  return 'off';
}

/**
 * A chave VAPID trafega em base64url; o PushManager exige bytes.
 * A conversão precisa repor o padding que o base64url omite.
 *
 * Devolve ArrayBuffer, e não Uint8Array: desde o TS 5.7 o Uint8Array é genérico
 * sobre o buffer e o de `new Uint8Array(n)` não satisfaz o BufferSource exigido
 * por `applicationServerKey`.
 */
function urlBase64ToUint8Array(base64: string): ArrayBuffer {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const normal = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(normal);
  const buf = new ArrayBuffer(raw.length);
  const view = new Uint8Array(buf);
  for (let i = 0; i < raw.length; i++) view[i] = raw.charCodeAt(i);
  return buf;
}
